import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';

const INTERNAL_CHANNEL_ID = 'internal-updates';

export const usesNativeSystemNotifications = () => Capacitor.isNativePlatform();

export const NOTIFICATION_CATEGORY_ROUTES = {
  projects: '/projects',
  tasks: '/tasks',
  clients: '/clients',
  users: '/users',
  documents: '/documents',
  finance: '/finance',
  system: '/dashboard',
  chat: '/chat',
  calendar: '/calendar',
  workflow: '/tasks',
  expense_requests: '/expense-tickets',
  partner_quotes: '/partner-quotes',
  commissions: '/commissions',
  billing_documents: '/sales-docs',
  fiscal: '/fiscal',
  followups: '/tasks',
  quality: '/iso-9001',
  security: '/security-center',
  email: '/email-history'
};

const toNativeNotificationId = (sourceId) => {
  const numericId = Number(sourceId);
  if (Number.isSafeInteger(numericId) && numericId > 0 && numericId <= 2_147_483_647) return numericId;

  const text = String(sourceId || `${Date.now()}-${Math.random()}`);
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = ((hash << 5) - hash + text.charCodeAt(index)) | 0;
  }
  return Math.max(1, Math.abs(hash));
};

export const getNotificationRoute = (notification = {}) => {
  const explicitRoute = notification.route || notification.link || notification.url;
  const unsafeCharacters = typeof explicitRoute === 'string' && Array.from(explicitRoute).some((character) => character === '\\' || character.charCodeAt(0) <= 32);
  if (typeof explicitRoute === 'string' && /^\/(?!\/)/.test(explicitRoute) && !unsafeCharacters) return explicitRoute;
  return Object.prototype.hasOwnProperty.call(NOTIFICATION_CATEGORY_ROUTES, notification.category) ? NOTIFICATION_CATEGORY_ROUTES[notification.category] : '/notifications';
};

export const requestSystemNotificationPermission = async () => {
  if (!usesNativeSystemNotifications()) {
    if ('Notification' in window && Notification.permission === 'default') {
      return Notification.requestPermission();
    }
    return 'Notification' in window ? Notification.permission : 'unsupported';
  }

  if (Capacitor.getPlatform() === 'android') {
    await LocalNotifications.createChannel({
      id: INTERNAL_CHANNEL_ID,
      name: 'Internal updates',
      description: 'Tasks, projects, chat and internal system activity',
      importance: 4,
      visibility: 1,
      vibration: true
    });
  }

  const current = await LocalNotifications.checkPermissions();
  const status = ['prompt', 'prompt-with-rationale'].includes(current.display)
    ? await LocalNotifications.requestPermissions()
    : current;
  return status.display;
};

export const showSystemNotification = async (notification = {}) => {
  if (!usesNativeSystemNotifications()) {
    if ('Notification' in window && Notification.permission === 'granted') {
      return new Notification(notification.title || 'SMTLINKS', {
        body: notification.message || notification.body || '',
        icon: '/logo192.png',
        tag: notification.id?.toString(),
        badge: '/logo192.png'
      });
    }
    return null;
  }

  const permission = await LocalNotifications.checkPermissions();
  if (permission.display !== 'granted') return null;

  return LocalNotifications.schedule({
    notifications: [{
      id: toNativeNotificationId(notification.id),
      title: notification.title || 'SMTLINKS',
      body: notification.message || notification.body || 'You have a new update.',
      largeBody: notification.message || notification.body || undefined,
      channelId: Capacitor.getPlatform() === 'android' ? INTERNAL_CHANNEL_ID : undefined,
      sound: 'default',
      schedule: { at: new Date(Date.now() + 150) },
      autoCancel: true,
      extra: {
        notificationId: notification.id || null,
        route: getNotificationRoute(notification),
        category: notification.category || ''
      }
    }]
  });
};

export const addSystemNotificationActionListener = async (listener) => {
  if (!usesNativeSystemNotifications()) return { remove: async () => {} };
  return LocalNotifications.addListener('localNotificationActionPerformed', (event) => {
    listener({
      notificationId: event.notification?.extra?.notificationId,
      route: event.notification?.extra?.route || '/notifications',
      category: event.notification?.extra?.category || ''
    });
  });
};

export const removeDeliveredSystemNotification = async (sourceId) => {
  if (!usesNativeSystemNotifications() || sourceId === undefined || sourceId === null) return;
  await LocalNotifications.removeDeliveredNotifications({
    notifications: [{ id: toNativeNotificationId(sourceId) }]
  });
};

export const clearDeliveredSystemNotifications = async () => {
  if (!usesNativeSystemNotifications()) return;
  const pending = await LocalNotifications.getPending();
  if (pending.notifications.length) {
    await LocalNotifications.cancel({
      notifications: pending.notifications.map(({ id }) => ({ id }))
    });
  }
  await LocalNotifications.removeAllDeliveredNotifications();
};
